'use client';

/**
 * Post-payment "add Menuraq to your home screen" modal.
 *
 * Shown once, shortly after a payment confirms , the only moment a guest has a
 * concrete reason to want the app on their home screen, because it is the
 * moment they start waiting for food.
 *
 * The steps are resolved per device (see lib/pwa/install-platform). This is not
 * decoration: only Chromium can offer a real one-tap system dialog, and every
 * other platform hides the action behind a differently-named menu. Showing an
 * iPhone guest an Android instruction, or vice versa, is worse than showing
 * nothing , they look for a control that isn't there and conclude the product
 * is broken.
 *
 * It never appears when the app is already installed, and never again once
 * dismissed or completed.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Loader2, MoreVertical, Plus, Share, Smartphone } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAddToHomeScreen } from '@/hooks/useAddToHomeScreen';
import { pushNeedsInstall } from '@/lib/pwa/install-platform';

const SEEN_KEY = 'mnrq:a2hs-modal:v1';
/** Long enough that the modal doesn't collide with the receipt painting, short
 *  enough that it still reads as part of "you've paid, here's what's next". */
const APPEAR_DELAY_MS = 2500;

type Step = { icon: React.ReactNode; text: React.ReactNode };

function GlyphStep({ icon, text }: Step) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full border border-[var(--cs-border)] bg-[var(--color-bg-sunken)]/60 text-[var(--color-text-primary)]">
        {icon}
      </span>
      <span className="pt-1 text-sm leading-relaxed text-[var(--color-text-secondary)]">{text}</span>
    </li>
  );
}

export function AddToHomeScreenModal({ delayMs = APPEAR_DELAY_MS, open: controlledOpen, onOpenChange, appName = 'Menuraq' }: {
  delayMs?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  appName?: string;
}) {
  const { platform, installed, promptInstall } = useAddToHomeScreen();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const markSeen = useCallback(() => {
    try {
      window.localStorage.setItem(SEEN_KEY, '1');
    } catch {
      /* storage disabled , it may reappear once, which is tolerable */
    }
  }, []);

  useEffect(() => {
    if (controlledOpen !== undefined || installed) return;
    if (platform.kind === 'installed' || platform.kind === 'unsupported') return;

    try {
      if (window.localStorage.getItem(SEEN_KEY) === '1') return;
    } catch {
      /* fall through and show it */
    }

    /* Mark it seen at the moment it is *shown*, not when it closes.
       Radix fires onOpenChange(false) for incidental closes too , an unmount, a
       route change, a reload mid-modal , so recording it on close burned the
       one showing without the guest ever laying eyes on it. Recording on open
       makes "once" mean once, whatever happens next. */
    const timer = setTimeout(() => {
      markSeen();
      setOpen(true);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [controlledOpen, installed, platform.kind, delayMs, markSeen]);

  const close = useCallback((next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  }, [onOpenChange]);

  const onNativeInstall = useCallback(async () => {
    setBusy(true);
    try {
      await promptInstall();
    } finally {
      setBusy(false);
      close(false);
    }
  }, [promptInstall, close]);

  /* Why the guest should care. On iOS this is the *only* route to order
     notifications, so the reason is concrete; everywhere else push already
     works in the browser and installing is just convenience. Saying the
     stronger thing on a device where it isn't true would be a lie. */
  const rationale = useMemo(
    () =>
      pushNeedsInstall()
        ? 'On iPhone and iPad, order notifications only work from the Home Screen. Add this place, then enable order notifications when asked.'
        : 'Keep this place one tap away to browse the menu and return to your order.',
    [],
  );

  const steps: Step[] = useMemo(() => {
    switch (platform.kind) {
      case 'ios-safari':
        return [
          { icon: <Share className="size-4" aria-hidden="true" />, text: <>Tap <span className="font-medium text-[var(--color-text-primary)]">Share</span> in Safari&rsquo;s toolbar</> },
          { icon: <Plus className="size-4" aria-hidden="true" />, text: <>Choose <span className="font-medium text-[var(--color-text-primary)]">Add to Home Screen</span>, then tap <span className="font-medium text-[var(--color-text-primary)]">Add</span></> },
          { icon: <Check className="size-4" aria-hidden="true" />, text: <>Open {appName} from your Home Screen</> },
        ];
      case 'ios-other':
        return [
          { icon: <Share className="size-4" aria-hidden="true" />, text: <>Tap the <span className="font-medium text-[var(--color-text-primary)]">Share</span> button in {platform.browser}</> },
          { icon: <Plus className="size-4" aria-hidden="true" />, text: <>Choose <span className="font-medium text-[var(--color-text-primary)]">Add to Home Screen</span>, then tap <span className="font-medium text-[var(--color-text-primary)]">Add</span></> },
          { icon: <Check className="size-4" aria-hidden="true" />, text: <>Open {appName} from your Home Screen</> },
        ];
      case 'samsung-internet':
        return [
          { icon: <MoreVertical className="size-4" aria-hidden="true" />, text: <>Open the browser <span className="font-medium text-[var(--color-text-primary)]">menu</span></> },
          { icon: <Plus className="size-4" aria-hidden="true" />, text: <>Tap <span className="font-medium text-[var(--color-text-primary)]">Add page to</span>, then <span className="font-medium text-[var(--color-text-primary)]">Home screen</span></> },
        ];
      case 'android-firefox':
        return [
          { icon: <MoreVertical className="size-4" aria-hidden="true" />, text: <>Open the browser <span className="font-medium text-[var(--color-text-primary)]">menu</span></> },
          { icon: <Plus className="size-4" aria-hidden="true" />, text: <>Tap <span className="font-medium text-[var(--color-text-primary)]">Install</span> (or <span className="font-medium text-[var(--color-text-primary)]">Add to Home screen</span>)</> },
        ];
      case 'android-other':
        return [
          { icon: <MoreVertical className="size-4" aria-hidden="true" />, text: <>Open the browser <span className="font-medium text-[var(--color-text-primary)]">menu</span></> },
          { icon: <Plus className="size-4" aria-hidden="true" />, text: <>Tap <span className="font-medium text-[var(--color-text-primary)]">Add to Home screen</span></> },
        ];
      case 'desktop-safari':
        return [
          { icon: <Share className="size-4" aria-hidden="true" />, text: <>Click <span className="font-medium text-[var(--color-text-primary)]">Share</span> in the Safari toolbar</> },
          { icon: <Plus className="size-4" aria-hidden="true" />, text: <>Choose <span className="font-medium text-[var(--color-text-primary)]">Add to Dock</span></> },
        ];
      default:
        return [];
    }
  }, [platform, appName]);

  if (installed || platform.kind === 'installed' || platform.kind === 'unsupported') return null;

  const isNative = platform.kind === 'native';

  return (
    <Dialog open={controlledOpen ?? open} onOpenChange={close}>
      {/* DialogContent ships responsive widths (sm:max-w-lg md:max-w-2xl
          lg:max-w-4xl); an unprefixed max-w loses to all three, so each
          breakpoint has to be overridden or this reads as a full-width sheet on
          a laptop. */}
      <DialogContent className="sm:max-w-[26rem] md:max-w-[26rem] lg:max-w-[26rem] max-h-[86svh] overflow-y-auto overscroll-contain">
        <DialogHeader>
          <span className="mb-1 flex size-11 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-bg-sunken)] text-[var(--color-text-primary)]">
            <Smartphone className="size-5" aria-hidden="true" />
          </span>
          <DialogTitle className="text-left text-[17px]">Add {appName} to your {platform.kind === 'desktop-safari' ? 'Dock' : 'Home Screen'}</DialogTitle>
          <DialogDescription className="text-left text-sm leading-relaxed">
            {rationale}
          </DialogDescription>
        </DialogHeader>

        {isNative ? (
          /* Chromium: a real system install dialog is one tap away, so written
             steps would be strictly worse than the button. */
          <Button className="mt-2 min-h-11 w-full" onClick={onNativeInstall} disabled={busy}>
            {busy ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Opening&hellip;
              </>
            ) : (
              <>
                <Plus className="size-4" aria-hidden="true" />
                Add to Home Screen
              </>
            )}
          </Button>
        ) : (
          <ol className="mt-1 space-y-3">
            {steps.map((step, i) => (
              <GlyphStep key={i} icon={step.icon} text={step.text} />
            ))}
          </ol>
        )}

        <Button variant="ghost" className="min-h-11 w-full" onClick={() => close(false)}>
          {isNative ? 'Not now' : 'Got it'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
